import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { Play, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Anxiety", "Depression", "Bullying", "Coping skills", "Mindfulness", "Family", "Substance use", "Identity"];

export default function SelfHelp() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [category, setCategory] = useState("All");
  const [playing, setPlaying] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", youtube_url: "", category: "Coping skills" });

  const load = async () => {
    try {
      const { data } = await api.get("/resources", { params: category !== "All" ? { category } : {} });
      setResources(data);
    } catch (e) { logger.warn("resources load failed:", e?.message); }
  };
  useEffect(() => { load(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const canManage = user?.role === "admin" || user?.role === "counsellor";

  const onAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post("/resources", form);
      toast.success("Resource added");
      setAddOpen(false);
      setForm({ title: "", description: "", youtube_url: "", category: "Coping skills" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await api.delete(`/resources/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-2">Self-help library</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3D405B] tracking-tight">Short videos. Big shifts.</h1>
            <p className="text-[#7A7D96] mt-2 max-w-xl">Curated by Hellofoundation Youth Mental Wellness & Support counsellors. Watch between sessions, or whenever you need a calmer minute.</p>
          </div>
          {canManage && (
            <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="selfhelp-add-btn">
              <Plus size={16} /> Add a video
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-7">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${category === c ? "bg-secondary text-white shadow-sm" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`} data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>

        {resources.length === 0 ? (
          <div className="brand-card p-10 text-center text-[#7A7D96]">No videos in this category yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((r) => (
              <div key={r.resource_id} className="brand-card brand-card-hover overflow-hidden group" data-testid={`resource-${r.resource_id}`}>
                <button onClick={() => setPlaying(r)} className="relative w-full aspect-video block overflow-hidden">
                  <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3D405B]/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play size={22} className="ml-1" />
                    </div>
                  </div>
                  <span className="absolute top-3 left-3 pill bg-card/95 text-[#3D405B]">{r.category}</span>
                </button>
                <div className="p-5">
                  <h3 className="font-bold text-[#3D405B] leading-snug">{r.title}</h3>
                  {r.description && <p className="text-sm text-[#7A7D96] mt-2 line-clamp-2">{r.description}</p>}
                  {user?.role === "admin" && (
                    <button onClick={() => onDelete(r.resource_id)} className="mt-3 text-xs text-destructive/80 hover:text-destructive inline-flex items-center gap-1" data-testid={`delete-resource-${r.resource_id}`}>
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {playing && (
        <div className="fixed inset-0 z-50 bg-[#3D405B]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPlaying(null)}>
          <div className="bg-card rounded-3xl overflow-hidden w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5">
              <div>
                <div className="overline text-primary">{playing.category}</div>
                <div className="font-extrabold text-[#3D405B] text-lg mt-1">{playing.title}</div>
              </div>
              <button onClick={() => setPlaying(null)} className="p-2 rounded-full hover:bg-[#3D405B]/5" data-testid="resource-modal-close"><X size={20} /></button>
            </div>
            <div className="aspect-video bg-black">
              <iframe
                title={playing.title}
                src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1&rel=0`}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            {playing.description && <p className="p-5 text-sm text-[#5F627D]">{playing.description}</p>}
          </div>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-[#3D405B]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAddOpen(false)}>
          <form onSubmit={onAdd} className="bg-card rounded-3xl w-full max-w-lg p-7 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-resource-form">
            <h3 className="text-2xl font-extrabold text-[#3D405B]">Add a self-help video</h3>
            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">YouTube URL</label>
              <input required value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" data-testid="resource-url-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Title</label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" data-testid="resource-title-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none">
                {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Short description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none resize-none" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAddOpen(false)} className="px-5 py-2.5 rounded-full font-semibold text-[#3D405B] hover:bg-[#3D405B]/5">Cancel</button>
              <button type="submit" className="px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="resource-submit-btn">Add video</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
